import { FormEditTabType, getWebPath } from '@doss/common-all'
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { Option } from '~/@/components/inputs/selection/utils'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { Button } from '~/@/components/ui/button'
import {
  BreadcrumbObjectType,
  GetFormVariantsDocument,
  GetFormVariantsQuery,
  useCreateFormVariantMutation,
  useGetFormVariantsQuery,
} from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import { Modal } from '../ui/molecules/modal/Modal'
import { useFormTabSettings } from './form-settings-tab/FormSettingsTabProvider'

interface FormVariantSwitcherProps {
  formId: string
}

export const FormVariantSwitcher = ({ formId }: FormVariantSwitcherProps) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTypedTranslation()
  const snackbar = useDossSnackbar()
  const currentVariantId = searchParams.get('variant') || 'base'
  const { isFormV2 } = useFormTabSettings()
  const [isCreateDialogShowing, setIsCreateDialogShowing] = useState(false)
  const [newVariantName, setNewVariantName] = useState('')

  const isNameEmpty = !newVariantName.trim()

  const { data: variantsData, loading: variantsLoading } = useGetFormVariantsQuery({
    variables: { formId },
  })

  const [createVariant, { loading: createVariantLoading }] = useCreateFormVariantMutation({
    update(cache, { data }) {
      if (!data?.createFormVariant) {
        return
      }

      const existingVariants: GetFormVariantsQuery | null = cache.readQuery({
        query: GetFormVariantsDocument,
        variables: { formId },
      })

      cache.writeQuery({
        query: GetFormVariantsDocument,
        variables: { formId },
        data: {
          formVariants: existingVariants?.formVariants
            ? [...existingVariants.formVariants, data.createFormVariant]
            : [data.createFormVariant],
        },
      })
    },
    onCompleted: () => {
      snackbar.showSuccess(t('form.variant.created_successfully'))
    },
    onError: (error) => {
      snackbar.showError(t('form.variant.create_failed', { error: error.message }))
    },
  })

  const options = useMemo<Option[]>(() => {
    const baseOption: Option = { value: 'base', label: t('form.variant.base_form') }
    const variantOptions: Option[] =
      variantsData?.formVariants.map((variant) => ({
        value: variant.id,
        label: variant.name,
      })) || []
    const createOption: Option = {
      value: 'create',
      label: t('form.variant.create_new_variant'),
    }
    return [baseOption, ...variantOptions, createOption]
  }, [variantsData, t])

  const handleVariantChange = (value: string | undefined) => {
    if (value === 'create') {
      setIsCreateDialogShowing(true)
      return
    }

    navigate(
      getWebPath({
        objectType: BreadcrumbObjectType.FORM,
        objectId: formId,
        settings: true,
        settingsParams: {
          tab: FormEditTabType.FORM_CONTENT,
        },
        variantId: value === 'base' ? undefined : value,
      }),
    )
  }

  const handleCloseDialog = () => {
    setIsCreateDialogShowing(false)
    setNewVariantName('')
  }

  const handleCreateVariant = async () => {
    if (isNameEmpty) {
      snackbar.showError(t('form.variant.name_required'))
      return
    }

    const result = await createVariant({
      variables: {
        input: {
          formId,
          name: newVariantName.trim(),
          variantInputs: [],
        },
      },
    })

    if (result.data?.createFormVariant) {
      handleCloseDialog()
      // Navigate to the newly created variant
      navigate(
        getWebPath({
          objectType: BreadcrumbObjectType.FORM,
          objectId: formId,
          settings: true,
          settingsParams: {
            tab: FormEditTabType.FORM_CONTENT,
          },
          variantId: result.data.createFormVariant.id,
        }),
      )
    }
  }

  if (!isFormV2) {
    return null
  }

  return (
    <>
      <Dropdown
        disabled={variantsLoading}
        inputProps={{
          className: 'text-right',
        }}
        isAutocompleteDisabled
        isBorderless
        onChange={handleVariantChange}
        options={options}
        showClearButton={false}
        value={currentVariantId}
      />

      {isCreateDialogShowing && (
        <Modal
          actions={
            <Button
              className="text-sm"
              disabled={isNameEmpty || createVariantLoading}
              onClick={handleCreateVariant}
            >
              {t('form.variant.create_variant')}
            </Button>
          }
          disableRestoreFocus
          onClose={handleCloseDialog}
          open={isCreateDialogShowing}
          title={t('form.variant.create_new_variant')}
        >
          <ShortTextInput
            onChange={(e) => setNewVariantName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCreateVariant()
              }
            }}
            placeholder={t('form.variant.name_placeholder')}
            value={newVariantName}
          />
        </Modal>
      )}
    </>
  )
}
